import {
  Listing,
  LISTING_GOING_CONCERN,
  LISTING_URGENT,
  ListingAdjustments,
  ListingDeadline,
  ListingType,
  LOCATION_STATE,
  LocationState,
  PAYMENT_OPTION,
  PaymentOption,
  PRICE_DESCRIPTION,
  REVENUE_MODEL,
  RevenueModel,
} from 'models/Listing';
import BoltIcon from 'icons/Bolt.icon';
import ProfitableGoingConcernIcon from 'icons/ProfitableGoingConcern.icon';
import utils, {
  getHasElementsInArray,
  removeUndefinedValuesFromArrayFields,
  transformCurrencyPropertiesToNumber,
  createRecordFromKeys,
} from 'utils';
import { DEFAULT_TIME_OPTION } from 'data/timeOptions';
import { t } from 'i18next';
import { CURRENCIES } from 'models/DealRoom';
import { REQUIRED_VISIBLE_INFO } from 'data/global';

export const extractRequiredVisibleInfoFields = (listing: Partial<Listing>) =>
  Object.fromEntries(
    Object.entries(listing).filter(
      ([key]) => !key.endsWith(REQUIRED_VISIBLE_INFO),
    ),
  );

export type RevenueModelMap = Partial<Record<RevenueModel, boolean>>;

export const revenueModelArrayToMap = (arr: RevenueModel[]): RevenueModelMap =>
  createRecordFromKeys(arr, true);

export const getRevenueModel = (
  revenueModel: RevenueModel[],
  defaultRevenueModel = [REVENUE_MODEL.AD_HOC],
) =>
  revenueModelArrayToMap(
    getHasElementsInArray(revenueModel) ? revenueModel : defaultRevenueModel,
  ) as RevenueModel[];

export const LISTING_TYPE_ROUTES: Record<ListingType, any> = {
  [LISTING_GOING_CONCERN]: {
    label: 'profitableGoingConcernLabel',
    route: '/profitable-going-concern',
    icon: ProfitableGoingConcernIcon,
  },
  [LISTING_URGENT]: {
    label: 'underExternalAdministrationLabel',
    route: '/under-external-administration',
    icon: BoltIcon,
  },
};

export const STEP_NAMES: Record<ListingType, string[]> = {
  [LISTING_URGENT]: [
    'Business info distressed listing',
    'Statement of information company under adminstration',
    'Listing options distressed listing',
    'Media package',
    'Checkout',
  ],
  [LISTING_GOING_CONCERN]: [
    'Business info profitable listing',
    'Statement of information profitable going concern',
    'Listing options profitable listing',
    'Media package',
    'Checkout',
  ],
};

export const LCW_PREVIEW_MODE_ROUTE = 'preview-mode';

/**
 * The only form values kept out of the local draft when a view unmounts:
 * their form shape differs from the listing's and processLCWValuesBeforeSubmit
 * has no reverse mapping for them - someoneToReplaceOwner and
 * contractedCustomers are YesNo in the form but boolean on the model, and
 * revenueModel is a checkbox map over an array. Merging those raw would
 * corrupt the draft that listingFormValues is derived from. Both flows still
 * map and send them on submit.
 */
export const VIEW_SWITCH_SKIPPED_FIELDS: string[] = [
  'someoneToReplaceOwner',
  'contractedCustomers',
  'revenueModel',
];

export const HIDE_CONTROLS_PAGES = ['teaser-preview'];

export const LCW_STEP_SUBMIT_TEXT: string[] = [
  'statementOfInformation',
  'listingPackage',
  'addOnService',
  'reviewAndPay',
  'publish',
];

export const ENABLE_TEASER_REQUIRED_FIELDS = [
  'revenue',
  'COGS',
  'EBIT',
  'EBITDA',
  'lessOverheads',
  'depreciationAndAmortisation',
  'accountingPeriod',
] as const;

export const TITLE_MAX_CHARACTERS = 90;

export const DESCRIPTION_MAX_CHARACTERS = 3000;

export const HIGHLIGHTS_MAX_CHARACTERS = 200;

export const PRODUCTS_AND_SERVICES_MAX_CHARACTERS = 200;

export const KEY_ASSETS_MAX_CHARACTERS = 200;

export const LISTING_CONTACT_BROKER_IMAGE_FILE_NAME =
  'listingContact.brokerImage';

export const LISTING_CONTACT_FIRM_LOGO_FILE_NAME = 'listingContact.firmLogo';

export const MONEY_PROPERTIES: (keyof Listing)[] = [
  'revenue',
  'EBITDA',
  'averageStockValue',
  'amountFrom',
  'amountTo',
  'amount',
  'COGS',
  'grossProfit',
  'lessOverheads',
  'operatingProfit',
  'depreciationAndAmortisation',
  'EBIT',
  'EBITDAMultiple',
  'totalAdjustments',
];

export const addTimeToDateDeadline = (deadline: ListingDeadline[]) =>
  deadline?.map(({ timelineDate, timelineTime, noFixedDate, ...rest }) => ({
    timelineDate:
      noFixedDate || !timelineDate
        ? timelineDate
        : utils.getDateInUserTimezone(timelineDate, +timelineTime),
    noFixedDate,
    ...rest,
  }));

export const mapDeadlineToFormValues = (deadline: ListingDeadline[]) =>
  deadline.map(({ noFixedDate, timelineDate, ...rest }) => {
    if (noFixedDate || !timelineDate) {
      return {
        timelineTime: DEFAULT_TIME_OPTION,
        timelineDate,
        noFixedDate,
        ...rest,
      };
    }

    const { hours } = utils.getTimeFromUserTimezone(timelineDate);

    return {
      timelineDate,
      timelineTime: `${hours}`,
      ...rest,
    };
  });

export const mapAdjustmentsPriceToNumberValues = (
  values: ListingAdjustments[],
) =>
  values
    ?.filter(
      (value) =>
        !!value &&
        !!Object.values(value).length &&
        Object.values(value).some(Boolean),
    )
    .map((el) => ({
      ...el,
      addBack: utils.getIntegerFromDottedNumber(String(el.addBack)),
      grossAmount: utils.getIntegerFromDottedNumber(String(el.grossAmount)),
      netAmount: utils.getIntegerFromDottedNumber(String(el.netAmount)),
    }));

export const calculatePriceDifference = (
  value1: string | number,
  value2: string | number,
) => {
  const valueFrom =
    typeof value1 === 'number'
      ? value1
      : utils.getIntegerFromDottedNumber(value1);
  const valueTo =
    typeof value2 === 'number'
      ? value2
      : utils.getIntegerFromDottedNumber(value2);

  const newValue = valueFrom - valueTo;

  if (newValue) {
    return utils.getDotedNumber(String(newValue).replace(/[,]/g, ''));
  } else {
    return '';
  }
};

export const calculatePriceDivision = (
  value1: string | number,
  value2: string | number,
) => {
  const valueFrom =
    typeof value1 === 'number'
      ? value1
      : utils.getIntegerFromDottedNumber(value1);
  const valueTo =
    typeof value2 === 'number'
      ? value2
      : utils.getIntegerFromDottedNumber(value2);

  const hasValue = valueFrom && valueTo && valueTo > 0;
  const newValue = valueFrom / valueTo;

  if (hasValue && newValue) {
    return utils.getDotedNumber(String(newValue).replace(/[,]/g, ''));
  } else {
    return '';
  }
};

export const calculatePriceArraySum = (values?: Array<string | number>) => {
  const newValue = (values || []).reduce<number>((acc, el) => {
    const value =
      typeof el === 'number' ? el : utils.getIntegerFromDottedNumber(el);

    if (value) return acc + value;

    return acc;
  }, 0);

  if (newValue) {
    return utils.getDotedNumber(String(newValue).replace(/[,]/g, ''));
  } else {
    return '';
  }
};

export const checkIfTeaserIsEnabled = (listing: Partial<Listing>) =>
  ENABLE_TEASER_REQUIRED_FIELDS.every(
    (el) => !!listing[el] || listing[el] === 0,
  );

export const validateLCW = (
  values: Partial<Listing> & {
    paymentOption: PaymentOption;
  },
) => {
  const {
    adjustments,
    accountingPeriod,
    priceDescription,
    paymentOption,
    locationState,
    currency,
    taxInvoice,
  } = values;

  const { name, legalEntity, address, NZBN, ABN } = taxInvoice || {};

  const isInvoice = paymentOption === PAYMENT_OPTION.INVOICE;
  const isFuture = priceDescription === PRICE_DESCRIPTION.FORECAST;
  const isNzLocation =
    checkIsNzLocation(locationState) || currency === CURRENCIES.NZD;
  const isTaxInvoiceNotEmpty = !!Object.values(taxInvoice || {}).filter(Boolean)
    .length;

  const accountingOptions = utils.generateNumberOfYears(5, isFuture);

  const accountingError =
    !!accountingPeriod &&
    !accountingOptions.some((el) => `fy${el}` === accountingPeriod)
      ? t('requiredField')
      : null;

  if (!isInvoice && isTaxInvoiceNotEmpty)
    return {
      taxInvoice: {
        ...(!name ? { name: t('requiredField') } : {}),
        ...(!legalEntity ? { legalEntity: t('requiredField') } : {}),
        ...(!address ? { address: t('requiredField') } : {}),
        ...(!NZBN && isNzLocation ? { NZBN: t('requiredField') } : {}),
        ...(!ABN && !isNzLocation ? { ABN: t('requiredField') } : {}),
      },
    };

  if (adjustments?.length) {
    const adjustmentsErrors = adjustments.map((adjustment) => {
      const adjustmentErrors: any = {};

      const shouldValidateAdjustments =
        !!adjustment?.name ||
        !!adjustment?.grossAmount ||
        !!adjustment?.netAmount ||
        !!adjustment?.addBack;

      if (shouldValidateAdjustments) {
        if (!adjustment?.name) adjustmentErrors.name = t('requiredField');

        if (!adjustment?.grossAmount)
          adjustmentErrors.grossAmount = t('requiredField');
        if (!adjustment?.addBack) adjustmentErrors.addBack = t('requiredField');
      }

      return adjustmentErrors;
    });

    return {
      adjustments: adjustmentsErrors,
      ...(accountingError && { accountingPeriod: accountingError }),
    };
  }

  if (accountingError) return { accountingPeriod: accountingError };

  return {};
};

export const checkIsNzLocation = (location: LocationState) =>
  location === LOCATION_STATE.NNZ || location === LOCATION_STATE.SNZ;

/**
 * Listing-shaped copy of the current form values, for merging back into the
 * local draft so a view switch never leaves a stale value behind - the slug,
 * the title, a figure entered in form view all have to be what preview mode
 * renders. Everything the form holds is carried; see
 * VIEW_SWITCH_SKIPPED_FIELDS for the three exceptions.
 */
export const pickCarriedValues = (values: Partial<Listing>) => {
  const picked = Object.entries(values || {}).reduce(
    (acc, [key, value]) =>
      VIEW_SWITCH_SKIPPED_FIELDS.includes(key) ? acc : { ...acc, [key]: value },
    {},
  );

  return processLCWValuesBeforeSubmit(picked);
};

export const processLCWValuesBeforeSubmit = (values: any) => {
  const {
    highlights,
    products,
    keyAssets,
    deadline,
    adjustments,
    ...restValues
  } = values;

  const existingArraysObject = Object.entries({
    highlights,
    products,
    keyAssets,
  }).reduce(
    (acc, [key, value]) => (value ? { ...acc, [key]: value } : acc),
    {},
  );

  const arrayFieldValues =
    removeUndefinedValuesFromArrayFields(existingArraysObject);

  const currencyValues = transformCurrencyPropertiesToNumber(
    restValues,
    MONEY_PROPERTIES,
  );

  return {
    ...restValues,
    ...currencyValues,
    ...arrayFieldValues,
    ...(adjustments?.length && {
      adjustments: mapAdjustmentsPriceToNumberValues(adjustments),
    }),
    ...(deadline?.length && {
      deadline: addTimeToDateDeadline(deadline),
    }),
  };
};
